/**
 * Konversi gambar ke WebP di BROWSER.
 *
 * Kenapa di sini, bukan di server: runtime server ini Bun, dan Bun tidak punya
 * encoder gambar. Mengerjakannya di server berarti menambah dependensi native berat
 * dan memindahkan beban CPU dekode-encode ke mesin yang melayani semua orang.
 * Browser sudah punya encoder WebP lewat canvas, gratis dan di mesin masing-masing.
 *
 * Server TIDAK mempercayai hasil ini begitu saja: byte yang masuk diperiksa di sana
 * (lihat server/src/lib/attachments.ts). Konversi di klien itu soal beban kerja, bukan soal
 * kepercayaan.
 */

/** Sisi terpanjang maksimal. Gambar lebih besar diperkecil sebelum di-encode. */
const MAX_SIDE = 1600
const QUALITY = 0.82

export interface WebpResult {
  file: File
  /** Gambar: URL objek untuk pratinjau. Dokumen: null. */
  previewUrl: string | null
  asalBytes: number
  hasilBytes: number
  /** Dokumen dikirim apa adanya; hanya gambar yang dikonversi. */
  kind: 'image' | 'document'
}

/** Ekstensi dokumen yang diterima server (lihat server/src/lib/attachments.ts). */
export const DOC_EXTENSIONS = ['pdf', 'docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt', 'csv', 'txt']

/** Satu berkas: gambar dikonversi, dokumen dilewatkan, sisanya ditolak di sini. */
export async function prepareAttachment(file: File): Promise<WebpResult> {
  if (isImage(file)) return toWebp(file)
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  if (!DOC_EXTENSIONS.includes(ext)) {
    throw new Error(`"${file.name}" bukan jenis berkas yang bisa dilampirkan.`)
  }
  // Dokumen tidak disentuh sama sekali: mengubahnya berarti mengubah isi yang
  // justru ingin dikirim orangnya.
  return { file, previewUrl: null, asalBytes: file.size, hasilBytes: file.size, kind: 'document' }
}

export function isImage(file: File): boolean {
  return file.type.startsWith('image/')
}

/**
 * Baca gambar lalu keluarkan versi WebP-nya.
 *
 * Memakai createImageBitmap, bukan <img> + onload, supaya dekodenya tidak menahan
 * thread utama dan gambar besar tidak membekukan halaman saat dipilih.
 */
export async function toWebp(file: File): Promise<WebpResult> {
  if (!isImage(file)) throw new Error(`"${file.name}" bukan gambar.`)

  const bitmap = await createImageBitmap(file)
  const skala = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height))
  const w = Math.max(1, Math.round(bitmap.width * skala))
  const h = Math.max(1, Math.round(bitmap.height * skala))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Browser ini tidak bisa memproses gambar.')
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', QUALITY),
  )
  // Browser yang tidak bisa meng-encode WebP mengembalikan PNG diam-diam, dan itu
  // akan ditolak server. Lebih baik gagal di sini dengan sebab yang jelas.
  if (!blob || blob.type !== 'image/webp') {
    throw new Error('Browser ini tidak mendukung WebP. Coba Chrome, Edge, atau Firefox versi baru.')
  }

  const nama = file.name.replace(/\.[^.]+$/, '') || 'gambar'
  const hasil = new File([blob], `${nama}.webp`, { type: 'image/webp' })
  return {
    file: hasil,
    previewUrl: URL.createObjectURL(blob),
    asalBytes: file.size,
    hasilBytes: hasil.size,
    kind: 'image',
  }
}
